import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Grid,
  Paper,
  Button,
  Container,
  IconButton,
  Tooltip,
  Breadcrumbs,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  ListItemIcon,
  Popover,
  PopoverOrigin,
  CircularProgress,
  useTheme
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import other from 'src/sections/_examples/extra/animate-view/other';

import CloseIcon from '@mui/icons-material/Close';
import {usePopover} from '@components/custom-popover';
import {useAppContext} from '@context/AppContext';
import FormConstants from '@shared/constants/FormConstants';
import axios, {CancelTokenSource} from 'axios';
import {
  Download,
  ChevronLeft,
  ChevronRight,
  CloudDownloadIcon
} from 'lucide-react';
import Info from '@mui/icons-material/Info';
import React, {useCallback, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useBoolean} from 'src/hooks/use-boolean';
import {
  LMS_TYPE,
  BASE_RECORD_ID,
  BASE_UNIQUE_ID,
  LMS_MODULE_ID,
  LMS_PREVIEW_ID,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  PARAMS_KEY,
  LMS_IS_ADMIN
} from 'src/routes/BaseRoute';
import EncryptUtils from 'src/utils/EncryptUtils';
import {DownloadUploadData} from '@shared/services/service-handlers/PrivateService';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';
import PreviewDoc from './ContentPreview';
import {useSettingsContext} from 'src/minimal-components/settings';
interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  type: string;
  sequence: number;
  files?: {
    id: string;
    name: string;
    ParentName: string;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    type: string;
    sequence: number;
    additionalFiles?: {id: string; name: string}[];
  }[];
}
type ContentComponentProps = {
  navData: any;
  openNav?: any;
  isOpenNav: boolean;
  applicationData?: ModuleData[];
  onCloseNav: VoidFunction;
  isLoading?: boolean;
  module?: string;
  menuItem?: any;
  renderCountBadge?: (countItem: any) => JSX.Element;
  menuListData?: any;
  filtersData?: any;
  setExternalUrl?: any;
  setVisitDetails?: any;
  applicationName?: string;
  onFileChange?(value: any): void;
  onModuleChange?(value: any): void;
};
const styles = {
  typography: 'body2',
  alignItems: 'center',
  color: 'text.primary',
  display: 'inline-flex'
};
const stylesend = {
  typography: 'body2',
  alignItems: 'center',
  display: 'inline-flex',
  cursor: 'default',
  pointerEvents: 'none',
  color: 'text.disabled'
};
const ContentViewer = (props: ContentComponentProps) => {
  const {
    navData,
    isOpenNav,
    onCloseNav,
    isLoading,
    module,
    menuItem,
    renderCountBadge,
    applicationData,
    onFileChange,
    onModuleChange
  } = props;
  const location = useLocation();
  const {uiFieldsvals} = location.state || {};
  //console.log('location.state', location.state);
  const nav = useBoolean();
  const theme = useTheme();
  const navigate = useNavigate();
  const settings = useSettingsContext();
  const isMini = settings.themeLayout === 'mini';
  // console.log('settings.themeLayout', settings.themeLayout, isMini);
  const {
    appState: {applicationName}
  } = useAppContext();
  const {currentSlide, setCurrentSlide} = useModulePreviewContext();
  const {ModulePreviewData, SetModulePreviewData} = useModulePreviewContext();
  const {selectedPreviewFile, setSelectedPreviewFile} =
    useModulePreviewContext();
  function useQuery() {
    const {search} = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const source = React.useRef<CancelTokenSource | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const popover = usePopover();
  const query = useQuery();
  const lmstype = EncryptUtils.getDecryptedData(location?.search, LMS_TYPE);
  const section = query.get('section');
  const subSection = query.get('subsection');
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const fileId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_UNIQUE_ID
  );
  const moduleId = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_MODULE_ID
  );
  const previewId = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_PREVIEW_ID
  );
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'attachment-popover' : undefined;
  const onRoleChange = async () => {
    settings.onUpdate('themeLayout', 'vertical');
    const paramsKey = EncryptUtils.setEncryptedParams({
      [BASE_RECORD_ID]: uniqueId,
      [LMS_TYPE]: lmstype,
      [LMS_IS_ADMIN]: lmsisAdmin
    });
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${FormConstants.MODULE.COURSE_CATALOG}/new?${PARAMS_KEY}=${paramsKey}&section=module`,
      {
        state: {}
      }
    );
  };
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setAnchorEl(event.currentTarget);
      setPopoverPosition({
        top: rect.bottom, // Moves popover below icon
        left: rect.left
      });
    },
    []
  );
  const isNextDisabled =
    ModulePreviewData &&
    !ModulePreviewData.some(module =>
      module.files?.some(
        file => !file.fileexpanded && file.sequence > currentSlide
      )
    );
  const ContentViewerFiles = useMemo(() => {
    if (selectedPreviewFile) {
      //console.log('prieves', selectedPreviewFile);
      return <PreviewDoc selectedPreviewFiles={selectedPreviewFile} />;
    }
  }, [selectedPreviewFile]);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);
  const toggleFullscreen = () => {
    settings.onUpdate(
      'themeLayout',
      settings.themeLayout === 'vertical' ? 'mini' : 'vertical'
    );
  };
  const handlePrevious = () => {
    // Function to find the previous file or node in the structure
    const findPreviousNode = (
      modules: any[],
      currentNodeId: string,
      typeprevious:string
    ): any | null => {
      let previousNode: any | null = null;
      let foundCurrent = false;

      const traverseFiles = (nodes: any[]): any => {
        for (const node of nodes) {
          if (node.id === currentNodeId && node.type===typeprevious) {
            foundCurrent = true;
            return; // Stop traversal once the current node is found
          }

          // Keep track of last valid file before reaching the current one
          if (
            node.type === 'file' ||
            node.type === 'link' ||
            node.type === 'content' ||
            node?.type === 'assignment' ||
            node?.type === 'onlinetest'
          ) {
            previousNode = node;
          }

          if (node.files) {
            traverseFiles(node.files);
            if (foundCurrent) return; // Stop searching once the current node is found
          }
        }
      };

      for (const module of modules) {
        traverseFiles(module.files || []);
        if (foundCurrent) break; // Stop searching after finding the current node
      }

      return previousNode;
    };

    const currentNodeId = selectedPreviewFile?.id;
    const types=selectedPreviewFile?.type;
    if (!currentNodeId || !types) return; // Exit if no selected file

    // Find the previous file
    const previousNode = findPreviousNode(ModulePreviewData, currentNodeId,types);

    if (previousNode) {
      // Update the expanded states in the mainContent
      SetModulePreviewData((prevState: any[]) => {
        let previousFileFound = false;
        const updatedModules = prevState.map(module => {
          return {
            ...module,
            files: module.files?.map((file: any) => {
              if (file.id === previousNode.id && !previousFileFound && file.type===previousNode.type) {
                previousFileFound = true; // Mark the previous file as found
                return {...file, fileexpanded: true}; // Expand the previous file
              }
              return {...file, fileexpanded: false}; // Collapse all other files
            }),
            expanded: module.files?.some(
              (file: any) => file.id === previousNode.id && file.type===previousNode.type
            ) // Expand the module if any file is expanded
          };
        });
        return updatedModules;
      });

      // Set the selected preview file and reset the slide
      setSelectedPreviewFile(previousNode);
      //   onFileChange && onFileChange(previousNode);
      setCurrentSlide(0);
    } else {
      console.log('No previous file found'); // Log if no file is available
    }
  };
  const handleNext = () => {
    // Function to find the next file or node in the structure
    const findNextNode = (
      modules: any[],
      currentNodeId: string,
      types:string
    ): any | null => {
      let foundCurrent = false;

      for (const module of modules) {
        // Traverse files within the current module
        const traverseFiles = (nodes: any[]): any | null => {
          for (const node of nodes) {
            if (foundCurrent) {
              // Return the next valid node
              if (
                node.type === 'file' ||
                node.type === 'link' ||
                node.type === 'content' ||
                node?.type === 'assignment' ||
                node?.type === 'onlinetest'
              ) {
                return node;
              }
            }
            if (node.id === currentNodeId && node.type===types) {
              foundCurrent = true; // Mark the current node as found
            }
            if (node.files) {
              // Recursively search within child files
              const found = traverseFiles(node.files);
              if (found) return found;
            }
          }
          return null;
        };

        // Search within the current module
        const nextNode = traverseFiles(module.files || []);
        if (nextNode) return nextNode; // Return the next node if found
      }

      return null; // No next node found
    };
    const currentNodeId = selectedPreviewFile?.id;
    const types=selectedPreviewFile?.type;
    if (!currentNodeId ||!types) return; // Exit if no selected file

    // Find the next file
    const nextNode = findNextNode(ModulePreviewData, currentNodeId,types);

    if (nextNode) {
      // Update the expanded states in the mainContent
      SetModulePreviewData((prevState: any[]) => {
        let nextFileFound = false;
        const updatedModules = prevState.map(module => {
          return {
            ...module,
            files: module.files?.map((file: any) => {
              if (file.id === nextNode.id && !nextFileFound && file.type===nextNode.type) {
                nextFileFound = true; // Mark the next file as found
                return {...file, fileexpanded: true}; // Expand the next file
              }
              return {...file, fileexpanded: false}; // Collapse all other files
            }),
            expanded: module.files?.some((file: any) => file.id === nextNode.id ) // Expand the module if any file is expanded
          };
        });
        return updatedModules;
      });

      // Set the selected preview file and reset the slide
      setSelectedPreviewFile(nextNode);
      //   onFileChange && onFileChange(nextNode);
      setCurrentSlide(0);
    } else {
      console.log('No next file found'); // Log if no file is available
    }
  };
  const downloadFile = async (
    bloburl: string,
    name: string,
    blob_file_extension?: string
  ) => {
    try {
      if (source.current) {
        source.current.cancel();
      }
      source.current = axios.CancelToken.source();
      const response = await DownloadUploadData(bloburl);
      // eslint-disable-next-line camelcase
      // eslint-disable-next-line camelcase
      if (response) {
        let fileName = `${name}`;
        if (blob_file_extension) {
          fileName = `${fileName}.${blob_file_extension}`;
        }
        // console.log('file_extensions', file_extensions, extractedType[0]);
        // const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response);
        link.download = fileName.replace(/["']/g, ''); // Remove quotes if present
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };
  if (!selectedPreviewFile)
    return (
      <Box>
        None Selected{' '}
        <Tooltip title="Close">
          <IconButton onClick={() => onRoleChange()}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );

  return (
    <Box
      sx={{
        height: '90vh', // Full viewport height
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: 1,
        overflow: 'hidden'
      }}>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: {xs: 30, sm: 40} // Responsive height
        }}>
        {/* Top Section */}
        <Breadcrumbs separator={<Separator />} {...other}>
          <Box sx={styles}> {selectedPreviewFile?.overviewName} </Box>
          <Box sx={stylesend}> {selectedPreviewFile?.ParentName} </Box>
        </Breadcrumbs>
        <Tooltip title={!isMini ? 'Full Screen' : 'Small Screen'}>
          <IconButton onClick={toggleFullscreen}>
            {!isMini ? <FullscreenIcon /> : <FullscreenExitIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          height: {xs: 30, sm: 40} // Responsive height
        }}>
        <Box sx={{flex: 1, display: 'flex', alignItems: 'center'}}>
          <Typography
            variant="h6"
            sx={{
              fontSize: {xs: '1rem', sm: '1.25rem'} // Responsive font size
            }}>
            {selectedPreviewFile.name}
          </Typography>
          {selectedPreviewFile?.enable_file_download && (
            <Tooltip title="Download">
              <IconButton
                onClick={() =>
                  downloadFile(
                    selectedPreviewFile?.azure_blob_url,
                    selectedPreviewFile?.name,
                    selectedPreviewFile?.blob_file_extension
                  )
                }>
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {selectedPreviewFile?.additionalFiles &&
            selectedPreviewFile?.additionalFiles.length > 0 && (
              <Box>
                <Tooltip title="View Resources">
                  <IconButton onClick={handleClick} aria-describedby={id}>
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          {(selectedPreviewFile?.tye !== 'onlinetest' ||
            selectedPreviewFile?.tye !== 'assignment') && (
            <Tooltip title="info">
              <IconButton
                onClick={e => {
                  //   alert('Downloading ' + selectedPreviewFile.name);
                  setShowInfo(true);
                }}>
                <Info fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Tooltip title="Prev">
          <IconButton onClick={handlePrevious}>
            <ChevronLeft />
          </IconButton>
        </Tooltip>
        <Tooltip title="Next">
          <IconButton
            onClick={handleNext}
            disabled={isNextDisabled} // Disable button if no next file exists
          >
            <ChevronRight />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Middle Section */}
      <Box
        sx={{
          flex: 1,
          p: {xs: 2, sm: 3}, // Responsive padding
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          maxHeight: '82vh'
        }}>
        {ContentViewerFiles}
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={popoverPosition || {top: 0, left: 0}}
        sx={{
          mt: 1 // Pushes the popover slightly below the icon
        }}>
        <Box sx={{p: 2, minWidth: 300, maxWidth: 400}}>
          <Typography variant="subtitle2" color="textSecondary">
            RESOURCES
          </Typography>
          <List>
            {selectedPreviewFile?.additionalFiles &&
              selectedPreviewFile?.additionalFiles.map(
                (
                  items: {
                    name: string;
                    size: number;
                    blob_url: string;
                  },
                  index: number
                ) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <InsertDriveFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={items.name}
                      secondary={`${(items.size / (1024 * 1024)).toFixed(2)} MB`}
                    />
                    <IconButton
                      onClick={() => downloadFile(items.blob_url, items.name)}>
                      <CloudDownloadIcon />
                    </IconButton>
                  </ListItem>
                )
              )}
          </List>
        </Box>
      </Popover>

      {showInfo && (
        <Dialog
          fullWidth
          maxWidth="xs"
          open={showInfo}
          onClose={() => setShowInfo(false)}>
          <DialogTitle sx={{pb: 2}}>{selectedPreviewFile.name}</DialogTitle>

          {selectedPreviewFile && (
            <DialogContent sx={{typography: 'body2', minHeight: '22vh'}}>
              <Box sx={{display: 'grid', rowGap: 1}}>
                {selectedPreviewFile?.type && (
                  <Box sx={{display: 'flex', columnGap: 1}}>
                    <Typography sx={{fontWeight: '600'}}>Type:</Typography>
                    <Typography>{selectedPreviewFile?.type}</Typography>
                  </Box>
                )}

                <div>
                  {selectedPreviewFile?.description && (
                    <Box sx={{display: 'flex', columnGap: 1}}>
                      <Typography sx={{fontWeight: '600'}}>
                        Description:
                      </Typography>
                      <Typography>
                        {selectedPreviewFile?.description}
                      </Typography>
                    </Box>
                  )}
                </div>
                <div>
                  {selectedPreviewFile?.size && (
                    <Box sx={{display: 'flex', columnGap: 1}}>
                      <Typography sx={{fontWeight: '600'}}>Size:</Typography>
                      <Typography>{selectedPreviewFile?.size}</Typography>
                    </Box>
                  )}
                </div>
                <div>
                  {selectedPreviewFile?.created_at && (
                    <Box sx={{display: 'flex', columnGap: 1}}>
                      <Typography sx={{fontWeight: '600'}}>
                        Created Date:
                      </Typography>
                      <Typography>{selectedPreviewFile?.created_at}</Typography>
                    </Box>
                  )}
                </div>
              </Box>
            </DialogContent>
          )}
        </Dialog>
      )}

      {/* Bottom Section */}
      {/* <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: {xs: 50, sm: 60} // Responsive height
          }}>
          <IconButton onClick={() => setIsPlaying(!isPlaying)}>
            {isPlaying ? <Pause /> : <Play />}
          </IconButton>
          <IconButton
            onClick={() =>
              setCurrentSlide((prev: number) => Math.max(0, prev - 1))
            }>
            <ArrowUp />
          </IconButton>
          <IconButton
            onClick={() =>
              setCurrentSlide((prev: number) =>
                Math.min(
                  prev + 1,
                  (selectedPreviewFile.content?.length || 1) - 1
                )
              )
            }>
            <ArrowDown />
          </IconButton>
          <Typography
            sx={{
              fontSize: {xs: '0.8rem', sm: '1rem'} // Responsive font size
            }}>
            Slide {currentSlide + 1} of {selectedPreviewFile.content?.length}
          </Typography>
        </Box> */}
    </Box>
  );
};
export default ContentViewer;
function Separator() {
  return (
    <Box
      component="span"
      sx={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        bgcolor: 'text.disabled'
      }}
    />
  );
}
